<template>
  <v-container v-resize="getWindowWidth">
    <div
      v-if="isLoading"
      class="d-flex justify-center align-center"
      style="height: 100vh;"
    >
      <v-progress-circular
        indeterminate
        color="#2661FF"
        size="80"
      ></v-progress-circular>
    </div>
    <div v-else>
      <v-row>
        <v-col cols="12">
          <div
            class="text-h6 grey--text text--darken-3 font-weight-medium mb-2 ml-6"
            style="font-size: 20px !important;"
          >
            {{ $t('organization.organizations') }}
          </div>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-card class="rounded-lg" flat>
            <v-row class="py-2 px-2">
              <v-col
                cols="12"
                lg="3"
                md="4"
                sm="6"
                v-for="({ title, value, icon, color }, index) in cards"
                :key="index"
                class="px-6"
              >
                <ListCard
                  :title="title"
                  :value="stats[value]"
                  :icon="icon"
                  :color="color"
                />
              </v-col>
            </v-row>
          </v-card>
        </v-col>
      </v-row>
      <v-row class="mt-12">
        <v-col cols="12">
          <v-card class="rounded-lg" flat>
            <v-card-title
              class="text-h6 grey--text text--darken-3 font-weight-medium pt-8 mx-4"
              style="font-size: 20px !important;"
              >{{ $t('organization.list_organizations') }}
              <v-spacer />
              <v-btn
                color="#2661FF"
                class="mx-2 rounded-lg"
                @click="openCreateOrganization"
              >
                <span style="color: #fff;">
                  <v-icon>add</v-icon> {{ $t('organization.create') }}
                </span>
              </v-btn>
              <v-btn
                ref="button-open-export-dialog"
                @click="toggleDialogExport(true)"
                color="#2661FF"
                outlined
                class="mx-2 rounded-lg"
              >
                <v-icon left>cloud_download</v-icon>
                {{ $t('general.export') }}
              </v-btn>
              <v-btn
                @click="toggleGlobalBillingDialog"
                color="#2661FF"
                class="mx-2 rounded-lg"
              >
                <v-icon color="white" left>show_chart</v-icon>
                <span style="color: white;">{{
                  $t('organization_billing.global_billing')
                }}</span>
              </v-btn>
            </v-card-title>
            <v-divider />
            <v-card-text>
              <v-row class="pa-4">
                <v-col cols="12" sm="4" md="3" lg="3">
                  <v-text-field
                    v-model="organizationsTable.search"
                    prepend-inner-icon="search"
                    :label="$t('general.search')"
                    outlined
                    clearable
                    class="rounded-lg"
                    hide-details
                    @keyup.enter="getOrganizations"
                    @click:clear="clearSearch"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="4" md="3" lg="3">
                  <v-autocomplete
                    :items="countries"
                    v-model="organizationsTable.country"
                    :label="$t('profile.profile.country')"
                    item-text="name"
                    item-value="id"
                    outlined
                    clearable
                    hide-details
                    class="rounded-lg"
                    @change="getOrganizations"
                  >
                  </v-autocomplete>
                </v-col>
                <v-col cols="12" sm="4" md="3" lg="3">
                  <v-select
                    :items="status_select_options"
                    v-model="organizationsTable.status"
                    :label="$t('actions.filter_by_status')"
                    outlined
                    clearable
                    hide-details
                    class="rounded-lg"
                    @change="getOrganizations"
                  >
                  </v-select>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <v-data-table
                    :headers="organizationsTable.headers"
                    :items="organizationsTable.items"
                    :page.sync="organizationsTable.pagination.page"
                    :items-per-page.sync="
                      organizationsTable.pagination.itemsPerPage
                    "
                    :sort-by.sync="organizationsTable.pagination.sortBy"
                    :sort-desc.sync="organizationsTable.pagination.sortDesc"
                    :server-items-length="organizationsTable.total"
                    :footer-props="{
                      'items-per-page-options': [25, 50, 75, 100]
                    }"
                    :items-per-page-text="
                      $t('organization.table.values.footer')
                    "
                    :no-data-text="$t('organization.table.values.empty')"
                    :no-results-text="
                      $t('organization.table.values.empty_search')
                    "
                    @update:page="page => getOrganizations()"
                    @update:items-per-page="getOrganizations"
                    @update:sort-by="getOrganizations"
                    @update:sort-desc="getOrganizations"
                    must-sort
                  >
                    <template v-slot:item="{ item }">
                      <tr @click="openOrganizationDrawer(item)">
                        <td>
                          <v-icon :color="getStatusObject(item.status).color">{{
                            getStatusObject(item.status).icon
                          }}</v-icon>
                        </td>
                        <td>
                          {{ item.id }}
                        </td>
                        <td class="font-weight-medium">
                          {{ item.name }}
                        </td>
                        <td>
                          {{ item.country.name }}
                        </td>
                        <td>
                          {{
                            item.organization_plan
                              ? item.organization_plan.plan.display_name
                              : null
                          }}
                        </td>
                        <td class="d-flex align-center">
                          <v-tooltip bottom color="grey darken-4">
                            <template v-slot:activator="{ on }">
                              <v-btn
                                @click.native.stop="
                                  openOrganizationDrawer(item)
                                "
                                text
                                fab
                                x-small
                              >
                                <v-icon
                                  v-on="on"
                                  class="grey--text text--darken-3"
                                  size="24"
                                >
                                  visibility
                                </v-icon>
                              </v-btn>
                            </template>
                            <span>ver</span>
                          </v-tooltip>
                          <v-tooltip bottom color="grey darken-4">
                            <template v-slot:activator="{ on }">
                              <v-btn @click="edit" text fab x-small>
                                <v-icon
                                  v-on="on"
                                  class="grey--text text--darken-3"
                                  size="24"
                                >
                                  edit_square
                                </v-icon>
                              </v-btn>
                            </template>
                            <span>editar</span>
                          </v-tooltip>
                        </td>
                      </tr>
                    </template>
                  </v-data-table>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>
    <!-- ORGANIZATION VIEW -->
    <v-navigation-drawer
      :value="organizationDrawerState"
      :permanent="organizationDrawerState"
      temporary
      fixed
      right
      :width="width"
      style="transition: 0.5s cubic-bezier(0.4, 0, 0.2, 1);"
      class="grey lighten-4"
    >
      <OrganizationView />
    </v-navigation-drawer>
    <!-- ORGANIZATION CREATE -->
    <v-navigation-drawer
      :value="organizationDrawerStateForm"
      :permanent="organizationDrawerStateForm"
      temporary
      fixed
      right
      :width="width"
      style="transition: 0.5s cubic-bezier(0.4, 0, 0.2, 1);"
      class="grey lighten-4"
    >
      <OrganizationFormDrawer />
    </v-navigation-drawer>
    <!-- ORGANIZATION EDIT -->
    <v-navigation-drawer
      :value="organizationDrawerStateEdit"
      :permanent="organizationDrawerStateEdit"
      temporary
      fixed
      right
      :width="width2"
      style="transition: 0.5s cubic-bezier(0.4, 0, 0.2, 1);"
      class="bg-gray"
    >
      <OrganizationEditDrawer />
    </v-navigation-drawer>
    <!-- CREATE USER -->
    <v-navigation-drawer
      :value="organizationDrawerStateUser.value"
      :permanent="organizationDrawerStateUser.value"
      temporary
      fixed
      right
      :width="width2"
      style="transition: 0.5s cubic-bezier(0.4, 0, 0.2, 1);"
      class="grey lighten-4"
    >
      <OrganizationUserDrawer />
    </v-navigation-drawer>

    <!-- SETTINGS TAB -->
    <AmazonSES />
    <AmazonSESv2 v-if="isAmazonSESv2ConfigOpen" />
    <Whatsapp v-if="isWhatsappConfigOpen" />
    <PaymentLimiterConfig />
    <AntiFraudConfig />
    <PaymentsDrawer />

    <!-- GLOBAL BILLING -->
    <v-dialog
      v-model="globalBilling.globalBillingDialog"
      scrollable
      max-width="400px"
      left="0"
    >
      <v-card class="rounded-lg" flat>
        <v-card-title
          class="title d-flex justify-center mb-2 mt-1 font-weight-regular"
          >{{ $t('organization_billing.select_month') }}</v-card-title
        >
        <v-divider class="mb-4"></v-divider>
        <v-card-text class="d-flex flex-column justify-center mb-2">
          <v-date-picker
            v-model="globalBilling.billing_date"
            type="month"
            :max="globalBilling.monthLimit"
            color="#2661FF"
          ></v-date-picker>

          <v-btn
            color="#2661FF"
            min-width="100px"
            @click="calculateGlobalBilling"
            :disabled="globalBilling.isGlobalBillingLoading"
            :loading="globalBilling.isGlobalBillingLoading"
          >
            <span style="color: #fff;">
              {{ $t('actions.calculate') }}
            </span>
          </v-btn>
        </v-card-text>
      </v-card>
    </v-dialog>

    <DialogExport :module-options="['Organizations', 'Lead']" />
    <DialogMessage />
  </v-container>
</template>

<script>
import organizationMixin from '@/mixins/organization'
import ListCard from '@/components/Common/ListCard.vue'
import AmazonSES from '@/components/EmailAccount/AmazonSES'
import DialogExport from '@/components/Downloads/DialogExport'
import DialogMessage from '@/components/Downloads/DialogMessage'
import { mapActions, mapState, mapMutations, mapGetters } from 'vuex'
import OrganizationView from '@/components/Organization/OrganizationView'
import PaymentsDrawer from '@/components/Organization/Settings/PaymentsDrawer'
import AntiFraudConfig from '@/components/Organization/Settings/AntiFraudConfig'
import Whatsapp from '@/components/Organization/Settings/Integrations/Whatsapp.vue'
import PaymentLimiterConfig from '@/components/Organization/Settings/PaymentLimiterConfig'
import OrganizationFormDrawer from '@/components/Organization/Details/OrganizationFormDrawer'
import OrganizationEditDrawer from '@/components/Organization/Details/OrganizationEditDrawer'
import OrganizationUserDrawer from '@/components/Organization/Details/OrganizationUserDrawer'
import AmazonSESv2 from '../../components/Organization/Settings/Integrations/AmazonSESv2.vue'

export default {
  name: 'organizationView',
  components: {
    ListCard,
    Whatsapp,
    AmazonSES,
    AmazonSESv2,
    DialogExport,
    DialogMessage,
    PaymentsDrawer,
    AntiFraudConfig,
    OrganizationView,
    PaymentLimiterConfig,
    OrganizationFormDrawer,
    OrganizationEditDrawer,
    OrganizationUserDrawer
  },
  mixins: [organizationMixin],
  data() {
    return {
      isLoading: false,
      width: '96%',
      width2: '55%',
      cards: [
        {
          title: this.$t('organization.cards.created'),
          icon: 'cloud_upload',
          value: 'totalOrganizations',
          color: '#2661FF'
        },
        {
          title: this.$t('organization.cards.intern'),
          icon: 'corporate_fare',
          value: 'internalOrganizations',
          color: 'black'
        },
        {
          title: this.$t('organization.cards.actives'),
          icon: 'verified',
          value: 'activesOrganizations',
          color: '#039855'
        },
        {
          title: this.$t('organization.cards.pendings'),
          icon: 'schedule',
          value: 'pendingsOrganizations',
          color: 'grey'
        },
        {
          title: this.$t('organization.cards.past_due'),
          icon: 'alarm',
          value: 'pastDueOrganizations',
          color: '#F79009'
        },
        {
          title: this.$t('organization.cards.suspended'),
          icon: 'block',
          value: 'suspendedOrganizations',
          color: '#DC6803'
        },
        {
          title: this.$t('organization.cards.canceled'),
          icon: 'cancel',
          value: 'canceledOrganizations',
          color: '#F04438'
        },
        {
          title: this.$t('organization.cards.deleted'),
          icon: 'delete',
          value: 'deletedOrganizations',
          color: 'grey'
        }
      ],
      stats: {
        totalOrganizations: 0,
        internalOrganizations: 0,
        activesOrganizations: 0,
        pendingsOrganizations: 0,
        pastDueOrganizations: 0,
        suspendedOrganizations: 0,
        canceledOrganizations: 0,
        deletedOrganizations: 0
      },
      organizationsTable: {
        items: [],
        total: 0,
        search: null,
        country: null,
        language: null,
        plan: null,
        status: null,
        pagination: {
          page: 1,
          itemsPerPage: 100,
          sortBy: 'created_at',
          sortDesc: false
        },
        headers: [
          {
            text: this.$t('general.status'),
            value: 'status',
            class: 'header_color text-no-wrap',
            width: '',
            sortable: false
          },
          {
            text: 'ID',
            value: 'id',
            class: 'header_color text-no-wrap',
            width: '',
            sortable: true
          },
          {
            text: this.$t('organization.table.org_name'),
            value: 'contact_name',
            class: 'header_color text-no-wrap',
            width: '',
            sortable: false
          },
          {
            text: this.$t('profile.profile.country'),
            value: 'country.name',
            class: 'header_color text-no-wrap',
            width: '300px',
            sortable: false
          },
          {
            text: this.$t('organization_billing.license'),
            value: 'organization_plan.plan.name',
            class: 'header_color text-no-wrap',
            width: '',
            sortable: false
          },
          {
            text: this.$t('articles.table.actions'),
            class: 'header_color text-no-wrap',
            width: '',
            sortable: false
          }
        ]
      },
      globalBilling: {
        isGlobalBillingLoading: false,
        globalBillingDialog: false,
        billing_date: this.$moment()
          .startOf('month')
          .format('YYYY-MM'),
        monthLimit: this.$moment()
          .endOf('month')
          .format('YYYY-MM')
      }
    }
  },
  async created() {
    try {
      this.isLoading = true

      // Running all promises concurrently
      await Promise.all([
        this.getOrganizationsStats(),
        this.getOrganizations(),
        this.getPlans(),
        this.getModules(),
        this.getIntegrations(),
        this.getAddons(),
        this.getCountries(),
        this.getCurrencies()
      ])
    } catch (error) {
      console.log(error)
    } finally {
      this.isLoading = false
    }
  },
  watch: {
    organizations(newVal, oldVal) {
      if (this.orgId) {
        this.getOrganization(this.orgId)
      }
    },
    updateRequired(newVal, oldVal) {
      if (newVal) {
        this.getOrganizations()
      }
    }
  },
  computed: {
    ...mapState({
      orgId: ({ organization: { orgId } }) => orgId,
      organization: ({ organization: { organization } }) => organization,
      organizations: ({ organization: { organizations } }) => organizations,
      totalOrganizations: ({ organization: { totalOrganizations } }) =>
        totalOrganizations,
      countries: ({ countries: { countries } }) => countries,
      languages: ({ auth: { locales } }) => locales,
      plans: ({ plans: { plans } }) => plans,
      organizationDrawerState: ({ organization: { organizationDrawer } }) =>
        organizationDrawer,
      organizationDrawerStateForm: ({
        organization: { organizationDrawerNew }
      }) => organizationDrawerNew,
      organizationDrawerStateEdit: ({
        organization: { organizationDrawerEdit }
      }) => organizationDrawerEdit,
      organizationDrawerStateUser: ({
        organization: { organizationDrawerUser }
      }) => organizationDrawerUser,
      isWhatsappConfigOpen: ({ accounts: { isWhatsappConfigOpen } }) =>
        isWhatsappConfigOpen,
      isAmazonSESv2ConfigOpen: ({ accounts: { isAmazonSESv2ConfigOpen } }) =>
        isAmazonSESv2ConfigOpen
    }),
    ...mapGetters({
      updateRequired: 'organization/updateRequired'
    })
  },
  methods: {
    ...mapActions({
      getPlans: 'plans/getPlans',
      getAddons: 'organization/getAddons',
      getModules: 'organization/getModules',
      getCountries: 'countries/getCountries',
      getCurrencies: 'countries/getCurrencies',
      getAllOrganizations: 'organization/getAll',
      getOrganization: 'organization/getOrganization',
      getIntegrations: 'organization/getIntegrations',
      getOrganizationMembers: 'organization/getOrganizationMembers',
      fetchOrganizationsStats: 'organization/fetchOrganizationsStats',
      calculateBilling: 'billings/calculateBilling'
    }),
    ...mapMutations({
      setOrgId: 'organization/setOrgId',
      toggleDialogExport: 'downloads/toggleDialogExport',
      setWarningPayment: 'organization/setWarningPayment',
      setUpdateRequired: 'organization/setUpdateRequired',
      organizationDrawer: 'organization/organizationDrawer',
      organizationDrawerNew: 'organization/organizationDrawerNew',
      organizationDrawerEdit: 'organization/organizationDrawerEdit'
    }),
    getWindowWidth() {
      if (process.client) {
        this.width =
          window.innerWidth > 800 ? window.innerWidth * 0.94 : window.innerWidth
      }
    },
    async getOrganizationsStats() {
      const stats = await this.fetchOrganizationsStats()
      Object.assign(this.stats, stats)
    },
    async getOrganizations() {
      const {
        search,
        country,
        language,
        plan,
        status
      } = this.organizationsTable
      const {
        page,
        sortBy,
        sortDesc,
        itemsPerPage
      } = this.organizationsTable.pagination

      try {
        const { total } = await this.getAllOrganizations({
          page,
          sortBy,
          sortDesc,
          itemsPerPage,
          search,
          country,
          language,
          plan,
          status
        })
        this.organizationsTable.items = this.organizations
        this.organizationsTable.total = Number(total)
      } catch (error) {
        console.log(error)
      }
    },
    async clearSearch() {
      this.organizationsTable.search = null
      await this.getOrganizations()
    },
    openCreateOrganization() {
      this.organizationDrawerNew(true)
    },
    toggleGlobalBillingDialog() {
      this.globalBilling.globalBillingDialog = !this.globalBilling
        .globalBillingDialog
    },
    openOrganizationDrawer(organization) {
      this.setOrgId(organization.id)
      this.organizationDrawer(true)
    },
    edit() {
      this.organizationDrawerEdit(true)
    },
    async calculateGlobalBilling() {
      if (this.globalBilling.billing_date) {
        const billing_date = this.$moment(this.globalBilling.billing_date)
          .startOf('month')
          .utc()
          .format()

        this.globalBilling.isGlobalBillingLoading = true
        // TODO: MAKE THIS A LISTENER LIKE DOWNLOADS
        await this.calculateBilling({
          billing_date,
          is_global: true
        })

        this.globalBilling.isGlobalBillingLoading = false
        this.globalBilling.globalBillingDialog = false
        this.setUpdateRequired(true)
      } else {
        this.globalBilling.globalBillingDialog = false
      }
    }
  }
}
</script>

<style lang="scss">
.header_color {
  color: #2661ff !important;
}
</style>
